import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { IComboBase } from '../../../interfaces/base/base.interface';
import { CurrencyService } from '../../../services/currency.service';
import { TypeContractService } from '../../../services/type-contracts.service';
import { ContinuityContractsService } from '../../../services/continuity-contracts.service';
import { forkJoin, switchMap } from 'rxjs';
import { WorkingModeService } from '../../../services/working-modes.service';
import { ModalRejectComponent } from '../../../shared/modals/modal-reject/modal-reject.component';
import { EmployeeLocalService } from '../../services/employee.service';
import { ModalLoadingComponent } from '../../../shared/modals/modal-loading/modal-loading.component';
import { ModalSuccessComponent } from '../../../shared/modals/modal-success/modal-success.component';

@Component({
  selector: 'app-cesados-register-contract-modal',
  templateUrl: './cesados-register-contract-modal.component.html',
  styleUrl: './cesados-register-contract-modal.component.scss',
  providers: [DialogService]
})
export class CesadosRegisterContractModalComponent implements OnChanges {

  @Input() visible: boolean = false;
  @Input() employeeData: any | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() backStep = new EventEmitter<void>();
  @Output() registerComplete = new EventEmitter<void>();

  @ViewChild('pdfUploader') 
  pdfUploader: any;

  isLoading: boolean = false;

  isDisabledContract: boolean = true;

  filteredOptions: IComboBase[] = [];

  currencies: IComboBase[] = [];
  typeContracts: IComboBase[] = [];
  workingModes: IComboBase[] = [];
  continues: IComboBase[] = [];

  selectedTypeContract: IComboBase | null = null;
  selectedContinuity: IComboBase | null = null;
  selectedWorkingMode: IComboBase | null = null;
  selectedCurrency: IComboBase | null = null;

  selectedCurrencyCode: string | null = null;

  contractStartDate: Date | null = null;
  contractEndDate: Date | null = null;

  basicSalary: number | null = null;
  performanceBonus: number | null = null;
  salary: number | null = null;

  computerAllowance: number | null = null;
  internetAllowance: number | null = null;
  electricityAllowance: number | null = null;
  transportationAllowance: number | null = null;

  currentFile: File | null = null;
  selectedFileName: string | null = null;

  stateContractId: number | null = null;

  hasInteracted: { [key: string]: boolean } = {};

  constructor(
    private currencyService: CurrencyService,
    private typeContractService: TypeContractService,
    private workingModeService: WorkingModeService,
    private continuityContractsService: ContinuityContractsService,
    private dialogService: DialogService,
    private employeeLocalService: EmployeeLocalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.employeeData) {
      this.loadContractData();
    }
  }

  loadContractData(): void {
    this.isLoading = true;

    forkJoin({
      currencies: this.currencyService.comboCurrency(),
      typeContracts: this.typeContractService.comboTypeContract(),
      workingModes: this.workingModeService.comboWorkingMode(),
      continuesContracts: this.continuityContractsService.comboContinuityContracts(),
    }).subscribe({
      next: (data) => {
        this.currencies = data.currencies;
        this.typeContracts = data.typeContracts;
        this.workingModes = data.workingModes;
        this.continues = data.continuesContracts;

        this.selectedWorkingMode =
          this.workingModes.find(
            (w) => w.key.toString() === this.employeeData.workingModeId.toString()
          ) || null;

        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;

        console.error(err);
      },
    });
  }

  filterOptions(event: any, type: string): void {
    const query = event.query.toLowerCase();

    let options: IComboBase[] = [];

    switch (type) {
      case 'typeContract':
        options = this.typeContracts;
        break;

      case 'currency':
        options = this.currencies;
        break;

      default:
        console.error('Tipo no reconocido:', type);
        return;
    }

    this.filteredOptions = options.filter((option) =>
      option.text.toLowerCase().includes(query)
    );
  }

  onSelect(event: any, type: string): void {
    switch (type) {
      case 'typeContract':
        this.selectedTypeContract = event.value ?? null;
        break;

      case 'currency':
        this.selectedCurrency = event.value ?? null;

        this.selectedCurrencyCode =
          this.selectedCurrency?.key == '1'
            ? 'S/.'
            : '$';

        break;

      default:
        console.error('Tipo no reconocido:', type);
        return;
    }

    this.validateContractForm();
  }

  onChangeInput(event: any, type: string): void {
    switch (type) {
      case 'contractStartDate':
        this.contractStartDate = event;
        break;

      case 'contractEndDate':
        this.contractEndDate = event;
        break;

      case 'basicSalary':
        this.basicSalary = event;
        this.calculateSalary();
        break;

      case 'performanceBonus':
        this.performanceBonus = event;
        this.calculateSalary();
        break;

      case 'computerAllowance':
        this.computerAllowance = event;
        this.calculateSalary();
        break;

      case 'internetAllowance':
        this.internetAllowance = event;
        this.calculateSalary();
        break;

      case 'electricityAllowance':
        this.electricityAllowance = event;
        this.calculateSalary();
        break;

      case 'transportationAllowance':
        this.transportationAllowance = event;
        this.calculateSalary();
        break;
    }

    this.validateContractForm();
  }

  onCheckboxChange(value: IComboBase): void {
    if (this.selectedContinuity?.key === value.key) {
      this.selectedContinuity = null;
    } else {
      this.selectedContinuity = value;
    }

    this.validateContractForm();
  }

  calculateSalary() {
    let total = 0;

    if (this.basicSalary !== null) total += this.basicSalary;
    if (this.performanceBonus !== null) total += this.performanceBonus;

    if (this.selectedWorkingMode) {
      if (
        this.selectedWorkingMode.key == '1' ||
        this.selectedWorkingMode.key == '3'
      ) {
        if (this.computerAllowance) total += this.computerAllowance;
        if (this.internetAllowance) total += this.internetAllowance;
        if (this.electricityAllowance) total += this.electricityAllowance;
      }

      if (
        this.selectedWorkingMode.key == '2' ||
        this.selectedWorkingMode.key == '3'
      ) {
        if (this.transportationAllowance) total += this.transportationAllowance;
      }
    }

    this.salary = total;
    this.validateContractForm();
  }

  openFileUpload(type: string) {
    if (type === 'pdf' && this.pdfUploader) {
      this.pdfUploader.choose();
    } else {
      console.error('El fileUploader no está disponible.');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
    
      if (this.isValidFile(droppedFile)) {
        this.currentFile = droppedFile;
        this.selectedFileName = droppedFile.name;
        this.stateContractId = 2;
        this.validateContractForm();
      } else {
        this.currentFile = null;
        this.selectedFileName = null;
        this.stateContractId = 4;
        this.validateContractForm();

        this.dialogService.open(ModalRejectComponent, {
          header: '',
          data: {
            text: 'El archivo no es válido. Solo se permiten archivos PDF, DOC o DOCX de hasta 10 MB.',
            title: '¡Upss!',
            showButton: true,
            buttonText: 'Entendido',
          },
        });
      }
    }
  }

  onChangeFile(event: any) {
    const selectedFile = event.files[0];
    if (selectedFile) {
      const maxSize = 10 * 1024 * 1024;
      const allowedTypes = ['application/pdf'];

      if (!allowedTypes.includes(selectedFile.type)) {
        this.dialogService.open(ModalRejectComponent, {
          header: '',
          data: {
            text: 'El archivo no es válido. Solo se permiten archivos PDF.',
            title: '¡Upss!',
            showButton: true,
            buttonText: 'Entendido',
          },
        });
        this.currentFile = null;
        this.selectedFileName = null;
        this.stateContractId = 4;
        this.hasInteracted['currentFile'] = false;
        this.validateContractForm();
        return;
      }

      if (selectedFile.size > maxSize) {
        this.dialogService.open(ModalRejectComponent, {
          header: '',
          data: {
            text: 'El archivo es demasiado grande. El tamaño máximo permitido es de 10 MB.',
            title: '¡Upss!',
            showButton: true,
            buttonText: 'Entendido',
          },
        });
        this.currentFile = null;
        this.selectedFileName = null;
        this.stateContractId = 4;
        this.hasInteracted['currentFile'] = false;
        this.validateContractForm();
        return;
      }

      this.currentFile = selectedFile;
      this.selectedFileName = selectedFile.name;
      this.stateContractId = 2;
      this.validateContractForm();
      this.hasInteracted['currentFile'] = true;
    }
    this.validateContractForm();
  }

  isValidFile(file: File): boolean {
    const allowedExtensions = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedExtensions.includes(file.type)) {
      console.log('Tipo de archivo no permitido:', file.type);
      return false;
    }

    if (file.size > maxSize) {
      console.log('Archivo demasiado grande:', file.size);
      return false;
    }

    return true;
  }

  validateField(field: string) {
    this.hasInteracted[field] = true;
  }

  validateContractForm() {
    const baseValid = Boolean(
      this.selectedTypeContract &&
        this.selectedWorkingMode &&
        this.contractStartDate &&
        this.contractEndDate &&
        this.selectedContinuity &&
        this.selectedCurrency &&
        this.salary !== null &&
        this.basicSalary !== null &&
        this.performanceBonus !== null
    );

    if (!baseValid) {
      this.isDisabledContract = true;
      return;
    }

    let workingModeValid = true;

    if (this.selectedWorkingMode) {
      switch (String(this.selectedWorkingMode?.key)) {
        case '1': // Remoto
          workingModeValid =
            this.computerAllowance != null &&
            this.internetAllowance != null &&
            this.electricityAllowance != null;
          break;

        case '2': // Presencial
          workingModeValid = this.transportationAllowance != null;
          break;

        case '3': // Híbrido
          workingModeValid =
            this.computerAllowance != null &&
            this.internetAllowance != null &&
            this.electricityAllowance != null &&
            this.transportationAllowance != null;
          break;

        default:
          workingModeValid = false;
      }
    }

    let salaryValid = false;
    if (
      this.salary !== null &&
      this.basicSalary !== null &&
      this.performanceBonus !== null
    ) {
      salaryValid =
        this.salary > 0 &&
        this.salary >= this.basicSalary + this.performanceBonus;
    }

    this.isDisabledContract = !(baseValid && workingModeValid && salaryValid);
  }

  onBack(): void {
    this.visible = false;

    this.visibleChange.emit(this.visible);

    this.backStep.emit();
  }

  onSaveChanges(): void {
    const loadingRef = this.dialogService.open(ModalLoadingComponent, { closable: false });

    const updateEmployeeRequest = {
      companyId: this.employeeData.companyId,
      typeDocumentId: this.employeeData.typeDocumentId,
      employeeDocumentNumber: this.employeeData.employeeDocumentNumber,
      names: this.employeeData.names,
      lastNames: this.employeeData.lastNames,
      countryId: this.employeeData.countryId,
      cityId: this.employeeData.cityId,
      address: this.employeeData.address,
      email: this.employeeData.email,
      phone: this.employeeData.phone,
      phoneEmergency: this.employeeData.phoneEmergency,
      employeeState: 'true',
      reasonInactive: null,
      gender: this.employeeData.gender,
      birthDate: this.employeeData.birthDate,
      startDate: this.employeeData.startDate,
      terminationDate: null,
      jobPositionId: this.employeeData.jobPositionId,
      workingModeId: this.employeeData.workingModeId,
    };

    const updateEmployeeFormData = new FormData();

    const appendIfDefined = (formData: FormData, key: string, value: any) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    };

    Object.entries(updateEmployeeRequest).forEach(([key, value]) =>
      appendIfDefined(updateEmployeeFormData, key, value)
    );

    const relevantAllowances: any = {};

    switch (String(this.selectedWorkingMode?.key)) {
      case '1':
        relevantAllowances.computerAllowance = this.computerAllowance;
        relevantAllowances.internetAllowance = this.internetAllowance;
        relevantAllowances.electricityAllowance = this.electricityAllowance;
        break;

      case '2':
        relevantAllowances.transportationAllowance =
          this.transportationAllowance;
        break;

      case '3':
        relevantAllowances.computerAllowance = this.computerAllowance;
        relevantAllowances.internetAllowance = this.internetAllowance;
        relevantAllowances.electricityAllowance = this.electricityAllowance;
        relevantAllowances.transportationAllowance =
          this.transportationAllowance;
        break;
    }

    const createContractRequest: any = {
      userId: this.employeeData.employeeId,
      employeeId: this.employeeData.employeeId,
      typeContractId: this.selectedTypeContract?.key,
      currencyId: this.selectedCurrency?.key,
      continuityContractId: this.selectedContinuity?.key,
      workingModeId: this.selectedWorkingMode?.key,
      startDate: this.contractStartDate
        ?.toISOString()
        .replace('Z', ''),
      endDate: this.contractEndDate
        ?.toISOString()
        .replace('Z', ''),
      stateContractId: this.stateContractId,
      basicSalary: this.basicSalary,
      performanceBonus: this.performanceBonus,
      salary: this.salary,
      ...relevantAllowances,
    };

    const contractFormData = new FormData();

    Object.entries(createContractRequest).forEach(([key, value]) =>
      appendIfDefined(contractFormData, key, value)
    );

    if (this.currentFile) contractFormData.append('file', this.currentFile);

    this.employeeLocalService
      .createContract(contractFormData)
      .pipe(switchMap(() => this.employeeLocalService.updateEmployee(this.employeeData.employeeId, updateEmployeeFormData)))
      .subscribe({
        next: () => {
          loadingRef.close();

          this.registerComplete.emit();

          this.closeDialog();

          this.dialogService.open(ModalSuccessComponent, {
            header: '',
            data: {
              text: 'El colaborador fue reactivado correctamente.',
              title: '¡Éxito!',
              icon: 'pi pi-check-circle',
              showButton: true,
              buttonText: 'Entendido',
            },
          });
        },

        error: (err) => {
          loadingRef.close();

          let errorMessage = 'Lo sentimos, ocurrió un error. Por favor, inténtelo nuevamente.';

          if (err.error) {
            const backendError = err.error.error || err.error;

            errorMessage =
              typeof backendError === 'string'
                ? backendError
                : Object.values(backendError).join(' ');
          }

          console.error(err);

          this.dialogService.open(ModalRejectComponent, {
            header: '',
            data: {
              text: errorMessage,
              title: '¡Lo sentimos!',
              showButton: true,
              buttonText: 'Entendido',
            },
          });
        },
      })
  }

  resetForm(): void {
    this.selectedTypeContract = null;
    this.selectedContinuity = null;
    this.selectedCurrency = null;

    this.selectedCurrencyCode = null;

    this.contractStartDate = null;
    this.contractEndDate = null;

    this.basicSalary = null;
    this.performanceBonus = null;
    this.salary = null;

    this.computerAllowance = null;
    this.internetAllowance = null;
    this.electricityAllowance = null;
    this.transportationAllowance = null;

    this.currentFile = null;
    this.selectedFileName = null;
    this.stateContractId = null;

    this.hasInteracted = {};

    this.isDisabledContract = true;
  }

  closeDialog(): void {
    this.resetForm();

    this.visible = false;

    this.visibleChange.emit(this.visible);
  }
}
